import { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  fetchDashboardContacts,
  fetchDashboardEngagement,
  fetchDashboardWidgets,
} from '~/redux/actions/dashboard-actions';

import { normalizeDashboardContacts, normalizeDashboardEngagement } from '../utils';

const useDashboardData = ({
  dashboardWidgets,
  dashboardContacts,
  dashboardEngagement,
  duration,
}) => {
  const dispatch = useDispatch();
  const hasCachedDashboardData = dashboardWidgets?.mailPerformance != null;
  const [isLoading, setIsLoading] = useState(() => !hasCachedDashboardData);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasLoadedDashboardRef = useRef(hasCachedDashboardData);
  const hadCachedDashboardDataOnMountRef = useRef(hasCachedDashboardData);
  const activeDashboardRequestKeyRef = useRef(null);

  const loadDashboardData = useCallback(
    async (refreshCache = false) => {
      const requestKey = `${duration}:${refreshCache ? 'refresh' : 'load'}`;

      // React StrictMode runs mount effects twice in development. Reuse the
      // active request instead of fetching and rendering the dashboard twice.
      if (activeDashboardRequestKeyRef.current === requestKey) return;
      activeDashboardRequestKeyRef.current = requestKey;

      if (refreshCache) {
        setIsRefreshing(true);
      } else if (!hasLoadedDashboardRef.current) {
        setIsLoading(true);
      }

      try {
        await Promise.allSettled([
          dispatch(
            fetchDashboardWidgets({
              duration,
              ...(refreshCache ? { refreshCache: true } : {}),
            })
          ),
          dispatch(fetchDashboardContacts(refreshCache ? { refreshCache: true } : {})),
          dispatch(
            fetchDashboardEngagement({
              duration,
              ...(refreshCache ? { refreshCache: true } : {}),
            })
          ),
        ]);
        hasLoadedDashboardRef.current = true;
      } finally {
        // A newer duration may have started while this request was in flight.
        // Only the latest request owns the visible loading state.
        if (activeDashboardRequestKeyRef.current === requestKey) {
          activeDashboardRequestKeyRef.current = null;
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [dispatch, duration]
  );

  useEffect(() => {
    loadDashboardData(true);
  }, [loadDashboardData]);

  return {
    dashboardWidgets,
    contactsOverview: normalizeDashboardContacts({
      contactsData: dashboardContacts,
      hasContacts: dashboardWidgets?.gettingStarted?.contactsAdded === true,
    }),
    engagementOverview: normalizeDashboardEngagement({
      engagementData: dashboardEngagement,
    }),
    isLoading,
    isRefreshing,
    hadCachedDashboardDataOnMount: hadCachedDashboardDataOnMountRef.current,
    refreshDashboard: () => loadDashboardData(true),
  };
};

export default useDashboardData;
